import React, { createContext, useContext, useEffect, useState } from 'react';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  async function register(email, password, displayName, organization, role) {
  const result = await createUserWithEmailAndPassword(auth, email, password);
  await result.user.getIdToken(true);
  await setDoc(doc(db, 'users', result.user.uid), {
    uid: result.user.uid,
    displayName,
    email,
    organization: organization || '',
    role: role || 'other',
    plan: 'standard',
    permissions: ['read', 'write'],
    created_at: new Date().toISOString(),
  });
  // Send verification email via backend (Resend)
  try {
    await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/email/send-verification`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        uid: result.user.uid,
        email,
        displayName,
      }),
    });
    console.log('Verification email sent via Resend to:', email);
  } catch (err) {
    console.warn('Verification email failed:', err.message);
  }

  // Require email verification before granting access (matches login()).
  // The Firestore user doc and verification email are already created above,
  // so signing out here doesn't lose anything — the account persists.
  await signOut(auth);

  return result;
}

async function resetPassword(email) {
  const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/email/send-password-reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error('Failed to send reset email');
}
  async function login(email, password) {
    const result = await signInWithEmailAndPassword(auth, email, password);
    if (!result.user.emailVerified) {
      await signOut(auth);
      throw new Error('Please verify your email before signing in. Check your inbox for the verification link.');
    }
    return result;
  }

  async function logout() {
    await signOut(auth);
    setUserProfile(null);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        const docSnap = await getDoc(doc(db, 'users', user.uid));
        if (docSnap.exists()) setUserProfile(docSnap.data());
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const isPro = userProfile?.plan === 'professional' || userProfile?.plan === 'enterprise';
  const isEnterprise = userProfile?.plan === 'enterprise';

  const value = {
    currentUser,
    userProfile,
    login,
    register,
    logout,
    resetPassword,
    isPro,
    isEnterprise,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
